import { useState, useEffect, useRef, useCallback } from "react";

const diferenciais = [
  { icon: "ri-shield-check-line", title: "Sem Burocracia", desc: "Cuidamos de toda a papelada e processos. Você só precisa nos contar sua situação." },
  { icon: "ri-computer-line", title: "Atendimento Online", desc: "Atendemos de qualquer lugar do Brasil, pelo celular ou computador, sem sair de casa." },
  { icon: "ri-rocket-line", title: "Foco em Aprovação Rápida", desc: "Trabalhamos com agilidade para que você receba seu benefício o quanto antes." },
  { icon: "ri-chat-smile-3-line", title: "Linguagem Simples", desc: "Explicamos tudo de forma clara, sem termos jurídicos difíceis. Você entende cada passo." },
  { icon: "ri-money-dollar-circle-line", title: "Honorários Transparentes", desc: "Condições de honorários definidas com clareza, podendo variar conforme o tipo de caso, sempre sem surpresas." },
  { icon: "ri-team-line", title: "Atendimento Personalizado", desc: "Cada caso é único. Dedicamos atenção individual a cada cliente e família." },
  { icon: "ri-map-pin-line", title: "Atendimento em Todo Brasil", desc: "Atendemos trabalhadores rurais, do interior e de qualquer região do país." },
  { icon: "ri-time-line", title: "Disponibilidade", desc: "Estamos disponíveis para tirar suas dúvidas e acompanhar seu processo de perto." },
];

const publicoAtendido = [
  { icon: "ri-user-heart-line", label: "Pessoas de Baixa Renda", desc: "Garantimos acesso à justiça independente da condição financeira" },
  { icon: "ri-plant-line", label: "Trabalhadores Rurais", desc: "Especialistas em aposentadoria rural e benefícios do campo" },
  { icon: "ri-women-line", label: "Mulheres Grávidas", desc: "Salário-maternidade e proteção dos direitos da gestante" },
  { icon: "ri-user-3-line", label: "Idosos", desc: "LOAS/BPC e aposentadoria por idade para quem mais precisa" },
];

function useWindowWidth() {
  const [width, setWidth] = useState(typeof window !== "undefined" ? window.innerWidth : 1200);
  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return width;
}

export default function DiferenciaisSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const width = useWindowWidth();

  const isMobile = width < 640;
  const maxIndex = isMobile ? diferenciais.length - 1 : 0;

  const goTo = useCallback((index: number) => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex(Math.max(0, Math.min(index, maxIndex)));
    setTimeout(() => setIsAnimating(false), 400);
  }, [isAnimating, maxIndex]);

  const prev = () => goTo(currentIndex - 1);
  const next = () => goTo(currentIndex + 1);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            (entry.target as HTMLElement).style.opacity = "1";
            (entry.target as HTMLElement).style.transform = "translateY(0)";
          }
        });
      },
      { threshold: 0.1 }
    );
    const elements = sectionRef.current?.querySelectorAll(".reveal-dif");
    elements?.forEach((el, i) => {
      (el as HTMLElement).style.opacity = "0";
      (el as HTMLElement).style.transform = "translateY(24px)";
      (el as HTMLElement).style.transition = `opacity 0.5s ease ${(i % 4) * 0.1}s, transform 0.5s ease ${(i % 4) * 0.1}s`;
      observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  return (
    <>
      {/* Público Atendido */}
      <section className="py-20 md:py-24" style={{ backgroundColor: "#FFFFFF" }}>
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <span className="inline-block text-sm font-semibold tracking-widest uppercase mb-3" style={{ fontFamily: "Montserrat, sans-serif", color: "#0B4D6F" }}>
              Para Quem Atendemos
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: "Cinzel, serif", color: "#1A1A1A" }}>
              Público Atendido
            </h2>
            <div className="mx-auto" style={{ width: "60px", height: "3px", backgroundColor: "#0B4D6F", borderRadius: "2px" }} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {publicoAtendido.map((item) => (
              <div key={item.label} className="flex items-start gap-4 p-6 rounded-2xl" style={{ backgroundColor: "#F2ECD4" }}>
                <div className="w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "#0B4D6F" }}>
                  <i className={`${item.icon} text-2xl text-white`}></i>
                </div>
                <div>
                  <div className="font-semibold text-sm mb-1" style={{ fontFamily: "Montserrat, sans-serif", color: "#1A1A1A" }}>{item.label}</div>
                  <div className="text-xs leading-relaxed" style={{ fontFamily: "Montserrat, sans-serif", color: "#555" }}>{item.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Diferenciais */}
      <section
        id="diferenciais"
        ref={sectionRef}
        className="py-20 md:py-28 relative overflow-hidden"
        style={{ backgroundColor: "#0B4D6F" }}
      >
        <div className="absolute inset-0 opacity-5" style={{ backgroundImage: "radial-gradient(circle at 20% 50%, #F2ECD4 0%, transparent 50%), radial-gradient(circle at 80% 20%, #F2ECD4 0%, transparent 40%)" }} />
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8">
          <div className="reveal-dif text-center mb-16">
            <span className="inline-block text-sm font-semibold tracking-widest uppercase mb-3" style={{ fontFamily: "Montserrat, sans-serif", color: "#F2ECD4" }}>
              Por Que Nos Escolher
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 text-white" style={{ fontFamily: "Cinzel, serif" }}>
              Nossos Diferenciais
            </h2>
            <div className="mx-auto" style={{ width: "60px", height: "3px", backgroundColor: "#F2ECD4", borderRadius: "2px" }} />
          </div>

          {/* Mobile: Carousel */}
          <div className="block sm:hidden reveal-dif relative">
            {/* Prev Arrow */}
            <button
              onClick={prev}
              disabled={currentIndex === 0}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 z-10 w-10 h-10 rounded-full flex items-center justify-center cursor-pointer transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed"
              style={{ backgroundColor: "rgba(255,255,255,0.15)", border: "2px solid rgba(242,236,212,0.6)", color: "#F2ECD4" }}
              aria-label="Anterior"
            >
              <i className="ri-arrow-left-s-line text-xl"></i>
            </button>

            {/* Cards Track */}
            <div className="overflow-hidden mx-8">
              <div
                className="flex"
                style={{
                  transform: `translateX(${-currentIndex * 100}%)`,
                  transition: "transform 0.4s ease-in-out",
                }}
              >
                {diferenciais.map((item, idx) => (
                  <div
                    key={idx}
                    className="flex-shrink-0 w-full px-2"
                  >
                    <div className="flex flex-col items-center text-center p-6 rounded-2xl" style={{ backgroundColor: "rgba(255,255,255,0.07)" }}>
                      <div className="w-14 h-14 rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: "rgba(242,236,212,0.15)" }}>
                        <i className={`${item.icon} text-2xl`} style={{ color: "#F2ECD4" }}></i>
                      </div>
                      <h3 className="font-semibold text-base mb-2 text-white" style={{ fontFamily: "Montserrat, sans-serif" }}>{item.title}</h3>
                      <p className="text-sm leading-relaxed" style={{ fontFamily: "Montserrat, sans-serif", color: "rgba(255,255,255,0.8)" }}>{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Next Arrow */}
            <button
              onClick={next}
              disabled={currentIndex >= maxIndex}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 z-10 w-10 h-10 rounded-full flex items-center justify-center cursor-pointer transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed"
              style={{ backgroundColor: "rgba(255,255,255,0.15)", border: "2px solid rgba(242,236,212,0.6)", color: "#F2ECD4" }}
              aria-label="Próximo"
            >
              <i className="ri-arrow-right-s-line text-xl"></i>
            </button>

            {/* Dots */}
            <div className="flex justify-center gap-2 mt-6">
              {diferenciais.map((_, i) => (
                <button
                  key={i}
                  onClick={() => goTo(i)}
                  className="rounded-full cursor-pointer transition-all duration-300"
                  style={{
                    width: currentIndex === i ? "24px" : "8px",
                    height: "8px",
                    backgroundColor: currentIndex === i ? "#F2ECD4" : "rgba(242,236,212,0.35)",
                  }}
                  aria-label={`Ir para slide ${i + 1}`}
                />
              ))}
            </div>
          </div>

          {/* Desktop: Grid */}
          <div className="hidden sm:grid grid-cols-2 lg:grid-cols-4 gap-6">
            {diferenciais.map((item, idx) => (
              <div key={idx} className="reveal-dif flex flex-col items-center text-center p-6 rounded-2xl" style={{ backgroundColor: "rgba(255,255,255,0.07)" }}>
                <div className="w-14 h-14 rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: "rgba(242,236,212,0.15)" }}>
                  <i className={`${item.icon} text-2xl`} style={{ color: "#F2ECD4" }}></i>
                </div>
                <h3 className="font-semibold text-base mb-2 text-white" style={{ fontFamily: "Montserrat, sans-serif" }}>{item.title}</h3>
                <p className="text-sm leading-relaxed" style={{ fontFamily: "Montserrat, sans-serif", color: "rgba(255,255,255,0.8)" }}>{item.desc}</p>
              </div>
            ))}
          </div>

          {/* CTA forte */}
          <div className="reveal-dif mt-16 text-center rounded-3xl p-10 md:p-14" style={{ backgroundColor: "#F2ECD4" }}>
            <h3 className="text-2xl md:text-4xl font-bold mb-4" style={{ fontFamily: "Cinzel, serif", color: "#1A1A1A" }}>
              Descubra se Você Tem Direito ao Benefício
            </h3>
            <p className="text-base md:text-lg mb-8 max-w-xl mx-auto" style={{ fontFamily: "Montserrat, sans-serif", color: "#555" }}>
              Fale agora com nossa equipe especializada. Análise gratuita, sem compromisso.
            </p>
            <div className="flex justify-center">
              <a
                href="https://wa.me/5598992018216?text=Ol%C3%A1%2C%20gostaria%20de%20saber%20mais%20sobre%20meus%20direitos%20previdenci%C3%A1rios."
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-6 sm:px-10 py-4 sm:py-5 rounded-full text-white font-semibold text-sm sm:text-lg whitespace-nowrap cursor-pointer transition-all duration-300 hover:opacity-90 hover:-translate-y-0.5"
                style={{ backgroundColor: "#0B4D6F", fontFamily: "Montserrat, sans-serif", boxShadow: "0 8px 24px rgba(11,77,111,0.3)" }}
              >
                <i className="ri-whatsapp-line text-xl sm:text-2xl"></i>
                Iniciar Conversa no WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
